import { createReducer, createAction, createSelector } from "@reduxjs/toolkit";
import { call, put, takeLatest } from "redux-saga/effects";
import { fetchSamplesAggregate } from "../lib/api";
import isBefore from "date-fns/isBefore";
import isAfter from "date-fns/isAfter";

import { selectors as searchSelectors, numericRangeFields, components, dateRangeFields } from "./experimentSearch";

export const actions = {
  request_sample_aggregate_data: createAction("REQUEST_SAMPLE_AGGREGATE_DATA"),
  receive_sample_aggregate_data: createAction("RECEIVE_SAMPLE_AGGREGATE_DATA"),
  receive_sample_aggregate_data_failed: createAction("RECEIVE_SAMPLE_AGGREGATE_DATA_FAILED"),
};

export const constants = {
  LOADED: "LOADED",
  LOADING: "LOADING",
  ERROR: "ERROR",
  SALT_DELIMITER: "$@$",
};

export const initState = {
  loadingState: constants.LOADING,
  sample_data: [],
};

export const actionMap = {
  [actions.request_sample_aggregate_data]: (state) => {
    return { ...state, loadingState: constants.LOADING };
  },
  [actions.receive_sample_aggregate_data]: (state, action) => {
    const { sample_data } = action.payload;
    return { ...state, sample_data, loadingState: constants.LOADED };
  },
  [actions.receive_sample_aggregate_data_failed]: (state) => {
    return { ...state, loadingState: constants.ERROR };
  },
};

export const reducer = createReducer(initState, actionMap);

function everyNumberIsNaNOrInRangeForActiveFilters(searchState, sample) {
  return !numericRangeFields.some((field) => {
    return (
      searchState[field].checked &&
      Number.isFinite(sample[field]) &&
      (sample[field] < searchState[field].filterRange[0] || sample[field] > searchState[field].filterRange[1])
    );
  });
}

function everyChemicalIsChecked(searchState, sample) {
  return !components.some((component) => {
    return sample[component].some((chemical) => !searchState[component].checked[chemical]);
  });
}

function everyDateIsInRangeForActiveFilters(searchState, sample) {
  return !dateRangeFields.some((field) => {
    return (
      searchState[field].checked &&
      (isBefore(sample[field], searchState[field].filterRange[0]) ||
        isAfter(sample[field], searchState[field].filterRange[1]))
    );
  });
}

const getExperimentSamplesAggregate = (state) => state.experimentSamplesAggregate;
const getSampleData = (state) => getExperimentSamplesAggregate(state).sample_data;
const getSampleDataWithParsedDates = createSelector([getSampleData], (sample_data) => {
  return sample_data.map((r) => {
    return { ...r, sample_time: new Date(r.sample_time) };
  });
});
const getSampleIdMatchesFilters = createSelector(
  [searchSelectors.getExperimentSearchWtihParsedDates, getSampleDataWithParsedDates],
  (searchState, data) => {
    const matchById = data
      .filter((sample) => {
        return (
          everyNumberIsNaNOrInRangeForActiveFilters(searchState, sample) &&
          everyChemicalIsChecked(searchState, sample) &&
          everyDateIsInRangeForActiveFilters(searchState, sample)
        );
      })
      .reduce((acc, { sample_id }) => {
        acc[sample_id] = true;
        return acc;
      }, {});

    return (sample_id) => !!matchById[sample_id];
  }
);
const getSampleDataWithParsedDatesMatchingFilters = createSelector(
  [getSampleDataWithParsedDates, getSampleIdMatchesFilters],
  (data, isMatch) => data.filter(({ sample_id }) => isMatch(sample_id))
);
const getSampleDataSaltsAndSaltsById = createSelector([getSampleData], (sample_data) => {
  const saltStringsSet = new Set();
  const saltStringsBySampleId = {};
  for (let { sample_id, salts: sample_salts } of sample_data) {
    const saltString = sample_salts.join(constants.SALT_DELIMITER);
    saltStringsSet.add(saltString);
    saltStringsBySampleId[sample_id] = saltString;
  }

  return { saltStrings: [...saltStringsSet], saltStringsBySampleId };
});

export const selectors = {
  getExperimentSamplesAggregate,
  getSampleData,
  getSampleDataWithParsedDates,
  getSampleIdMatchesFilters,
  getSampleDataWithParsedDatesMatchingFilters,
  getSampleDataSaltsAndSaltsById,
};

function* onFetchSampleAggregateData(action) {
  try {
    const { accessToken } = action.payload;

    const {
      data: { samples_aggregate },
    } = yield call(fetchSamplesAggregate, { accessToken });
    //don't dispaly any data with only unsupported chemical types
    const sample_data = samples_aggregate.filter((d) => {
      d.salts = d.salts || [];
      d.polymers = d.polymers || [];
      d.solvents = d.solvents || [];
      d.monomers = d.monomers || [];

      return d.salts.length + d.polymers.length + d.solvents.length + d.monomers.length > 0;
    });

    yield put(actions.receive_sample_aggregate_data({ sample_data }));
  } catch (e) {
    console.error(e);
    yield put(actions.receive_sample_aggregate_data_failed());
  }
}

export const sagas = {
  watchEveryRequestSampleAggregateData: function* watchEveryRequestSampleAggregateData() {
    yield takeLatest(actions.request_sample_aggregate_data.toString(), onFetchSampleAggregateData);
  },
};
